/*
 * Lemonade Training Oy copy rights all right reserved.
 * Access Control Type Definitions
 *
 * Centralized access control constants for roles, tiers, and permissions.
 * Used by both frontend (hooks) and backend (cloud functions).
 */

/* ============================================================
 * ACCESS TIERS
 * Hierarchical numeric levels for simple "at least X" checks.
 * Using 100-gaps for future insertions without breaking changes.
 * ============================================================ */
export const ACCESS_TIER = {
  GUEST: 0,
  ATHLETE: 100,
  BOX_ATHLETE: 200, // Athlete with active box membership
  BOX_COACH: 300, // Coach employed by a box
  COACH: 400, // Independent coach
  BOX_ADMIN: 500, // Box owner/administrator
  LEMONADE_ADMIN: 900, // Lemonade staff
  SUPER_ADMIN: 1000, // Full system access
} as const;

export type AccessTier = (typeof ACCESS_TIER)[keyof typeof ACCESS_TIER];

/* ============================================================
 * ROLES
 * String identifiers for user roles.
 * Note: Backend UserConst.js has its own ROLE constant with different
 * format (camelCase keys, Title Case values) for legacy compatibility.
 * ============================================================ */
export const ROLE = {
  ATHLETE: "athlete",
  BOX_ATHLETE: "box_athlete",
  BOX_COACH: "box_coach",
  COACH: "coach",
  BOX_ADMIN: "box_admin",
  LEMONADE_ADMIN: "lemonade_admin",
  SUPER_ADMIN: "super_admin",
} as const;

export type Role = (typeof ROLE)[keyof typeof ROLE];

/* ============================================================
 * PERMISSIONS
 * Granular capabilities that can be checked via can() in hooks.
 * ============================================================ */
export const PERMISSION = {
  // === Workout & Programming ===
  VIEW_WORKOUTS: "view_workouts",
  CREATE_WORKOUTS: "create_workouts",
  EDIT_WORKOUTS: "edit_workouts",
  DELETE_WORKOUTS: "delete_workouts",
  PUBLISH_PROGRAMS: "publish_programs",

  // === Athlete Management ===
  VIEW_OWN_ATHLETES: "view_own_athletes", // Coach's own clients
  VIEW_BOX_ATHLETES: "view_box_athletes", // All athletes in their box
  MANAGE_ATHLETES: "manage_athletes", // Add/remove athlete relationships

  // === Box Portal ===
  VIEW_BOX_PORTAL: "view_box_portal",
  MANAGE_BOX_SETTINGS: "manage_box_settings",
  MANAGE_BOX_MEMBERS: "manage_box_members",
  INVITE_BOX_COACHES: "invite_box_coaches",
  VIEW_BOX_ANALYTICS: "view_box_analytics",

  // === Booking System ===
  BOOK_CLASSES: "book_classes",
  VIEW_CLASS_SCHEDULE: "view_class_schedule",
  MANAGE_CLASSES: "manage_classes", // Create/edit/cancel classes
  MANAGE_CLASS_TYPES: "manage_class_types",
  CHECK_IN_ATHLETES: "check_in_athletes",

  // === Coach Portal ===
  VIEW_COACH_PORTAL: "view_coach_portal",
  SELL_PROGRAMS: "sell_programs",

  // === Admin Portal ===
  VIEW_ADMIN_PORTAL: "view_admin_portal",
  MANAGE_COACHES: "manage_coaches", // Assign/revoke coach status
  MANAGE_BOXES: "manage_boxes", // Assign/revoke box status
  MANAGE_USERS: "manage_users", // General user management
  VIEW_PLATFORM_ANALYTICS: "view_platform_analytics",

  // === Debug & Development ===
  VIEW_DEBUG_SCREEN: "view_debug_screen", // Access to performance debug screen (super admin only)

  // === Athlete Radar & Movement Registry ===
  VIEW_ATHLETE_SCORES: "view_athlete_scores", // Coach: view athlete radar scores and profiles
  VIEW_MOVEMENT_REGISTRY: "view_movement_registry", // All authenticated: view movement registry
  MANAGE_MOVEMENT_REGISTRY: "manage_movement_registry", // Box Admin / Lemonade Admin: manage movement registry entries
  VIEW_GROUP_ANALYSIS: "view_group_analysis", // Box Admin (web only): run group analysis on athletes
} as const;

export type Permission = (typeof PERMISSION)[keyof typeof PERMISSION];

/* ============================================================
 * ROLE → PERMISSIONS MAPPING
 * Defines which permissions each role has by default.
 * ============================================================ */
export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  [ROLE.ATHLETE]: [PERMISSION.VIEW_WORKOUTS],

  [ROLE.BOX_ATHLETE]: [
    PERMISSION.VIEW_WORKOUTS,
    PERMISSION.BOOK_CLASSES,
    PERMISSION.VIEW_CLASS_SCHEDULE,
  ],

  [ROLE.BOX_COACH]: [
    PERMISSION.VIEW_WORKOUTS,
    PERMISSION.CREATE_WORKOUTS,
    PERMISSION.EDIT_WORKOUTS,
    PERMISSION.VIEW_BOX_ATHLETES,
    PERMISSION.VIEW_BOX_PORTAL,
    PERMISSION.VIEW_COACH_PORTAL,
    PERMISSION.VIEW_CLASS_SCHEDULE,
    PERMISSION.BOOK_CLASSES,
    PERMISSION.MANAGE_CLASSES,
    PERMISSION.CHECK_IN_ATHLETES,
    PERMISSION.VIEW_ATHLETE_SCORES,
    PERMISSION.VIEW_MOVEMENT_REGISTRY,
  ],

  [ROLE.COACH]: [
    PERMISSION.VIEW_WORKOUTS,
    PERMISSION.CREATE_WORKOUTS,
    PERMISSION.EDIT_WORKOUTS,
    PERMISSION.DELETE_WORKOUTS,
    PERMISSION.PUBLISH_PROGRAMS,
    PERMISSION.VIEW_OWN_ATHLETES,
    PERMISSION.MANAGE_ATHLETES,
    PERMISSION.VIEW_COACH_PORTAL,
    PERMISSION.SELL_PROGRAMS,
  ],

  [ROLE.BOX_ADMIN]: [
    PERMISSION.VIEW_WORKOUTS,
    PERMISSION.CREATE_WORKOUTS,
    PERMISSION.EDIT_WORKOUTS,
    PERMISSION.DELETE_WORKOUTS,
    PERMISSION.VIEW_BOX_ATHLETES,
    PERMISSION.MANAGE_ATHLETES,
    PERMISSION.VIEW_BOX_PORTAL,
    PERMISSION.MANAGE_BOX_SETTINGS,
    PERMISSION.MANAGE_BOX_MEMBERS,
    PERMISSION.INVITE_BOX_COACHES,
    PERMISSION.VIEW_BOX_ANALYTICS,
    PERMISSION.VIEW_CLASS_SCHEDULE,
    PERMISSION.BOOK_CLASSES,
    PERMISSION.MANAGE_CLASSES,
    PERMISSION.MANAGE_CLASS_TYPES,
    PERMISSION.CHECK_IN_ATHLETES,
    PERMISSION.VIEW_COACH_PORTAL,
    PERMISSION.VIEW_ATHLETE_SCORES,
    PERMISSION.VIEW_MOVEMENT_REGISTRY,
    PERMISSION.MANAGE_MOVEMENT_REGISTRY,
    PERMISSION.VIEW_GROUP_ANALYSIS,
  ],

  [ROLE.LEMONADE_ADMIN]: [
    // All permissions except platform-wide management
    PERMISSION.VIEW_WORKOUTS,
    PERMISSION.CREATE_WORKOUTS,
    PERMISSION.EDIT_WORKOUTS,
    PERMISSION.DELETE_WORKOUTS,
    PERMISSION.PUBLISH_PROGRAMS,
    PERMISSION.VIEW_OWN_ATHLETES,
    PERMISSION.VIEW_BOX_ATHLETES,
    PERMISSION.MANAGE_ATHLETES,
    PERMISSION.VIEW_BOX_PORTAL,
    PERMISSION.MANAGE_BOX_SETTINGS,
    PERMISSION.MANAGE_BOX_MEMBERS,
    PERMISSION.INVITE_BOX_COACHES,
    PERMISSION.VIEW_BOX_ANALYTICS,
    PERMISSION.BOOK_CLASSES,
    PERMISSION.VIEW_CLASS_SCHEDULE,
    PERMISSION.MANAGE_CLASSES,
    PERMISSION.MANAGE_CLASS_TYPES,
    PERMISSION.CHECK_IN_ATHLETES,
    PERMISSION.VIEW_COACH_PORTAL,
    PERMISSION.SELL_PROGRAMS,
    PERMISSION.VIEW_ADMIN_PORTAL,
    PERMISSION.MANAGE_COACHES,
    PERMISSION.MANAGE_BOXES,
    PERMISSION.VIEW_ATHLETE_SCORES,
    PERMISSION.VIEW_MOVEMENT_REGISTRY,
    PERMISSION.MANAGE_MOVEMENT_REGISTRY,
    PERMISSION.VIEW_GROUP_ANALYSIS,
  ],

  [ROLE.SUPER_ADMIN]: [
    // All permissions
    ...Object.values(PERMISSION),
  ],
} as const;

/* ============================================================
 * LEGACY ACCESS LEVEL MAPPING
 * Maps old numeric accessLevel values to new ACCESS_TIER.
 * Used for backward compatibility during migration.
 * ============================================================ */
export const LEGACY_ACCESS_LEVEL_TO_TIER: Record<number, AccessTier> = {
  0: ACCESS_TIER.ATHLETE,
  2: ACCESS_TIER.BOX_COACH,
  3: ACCESS_TIER.COACH,
  6: ACCESS_TIER.BOX_ADMIN,
  8: ACCESS_TIER.LEMONADE_ADMIN,
  10: ACCESS_TIER.SUPER_ADMIN,
};

/* ============================================================
 * HELPER: Get permissions for a role
 * ============================================================ */
export const getPermissionsForRole = (role: Role): readonly Permission[] => {
  return ROLE_PERMISSIONS[role] || [];
};

/* ============================================================
 * HELPER: Check if a role has a specific permission
 * ============================================================ */
export const roleHasPermission = (
  role: Role,
  permission: Permission,
): boolean => {
  const permissions = ROLE_PERMISSIONS[role];
  return permissions?.includes(permission) ?? false;
};
